import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GOOGLE_ADVERTISING_PRODUCTS } from 'util/links';

import { Box, Button, Divider, Grid, Link, Typography } from '@mui/material';
import { Container } from 'component/Container/Container';
import { SkipToContentLink } from 'component/SkipToContentLink/SkipToContentLink';
import { LangNamespaceEnum } from 'shared/enums/LangNamespaceEnum';
import { MAIN_CONTENT_ID } from 'shared/constants/mainContent';

import { RootBox, bannerButtonSx } from './CookieConsentPure.s';

export type CookieConsentPureProps = {
  onAccept: () => void;
  onReject: () => void;
};

export const CookieConsentPure: React.VFC<CookieConsentPureProps> = ({
  onAccept,
  onReject,
}: CookieConsentPureProps) => {
  const { t } = useTranslation(LangNamespaceEnum.SURVEY_UI_COMPONENTS);
  const [showCookies, setShowCookies] = useState<boolean>(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The banner is a blocking prompt, so move focus to its heading on mount — otherwise the survey
  // page steals focus and screen readers skip the banner entirely (L1S-1402).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <RootBox>
      <Container>
        <Typography mb={2} variant="h6" component="h2" ref={headingRef} tabIndex={-1}>
          {t('CookieConsent.title')}
        </Typography>
        <SkipToContentLink targetId={MAIN_CONTENT_ID} />
        <Typography variant="body1" mb={1}>
          {t('CookieConsent.description[1]')}
        </Typography>
        <Typography variant="body1" mb={1}>
          {t('CookieConsent.description[2]')}
        </Typography>
        <Typography variant="body1" mb={1}>
          {t('CookieConsent.description[3]')}
        </Typography>
        <Grid container alignItems="center" spacing={2} mt={0}>
          <Grid item>
            <Button
              color="secondary"
              onClick={onAccept}
              size="small"
              variant="outlined"
              sx={bannerButtonSx}
            >
              {t('CookieConsent.acceptCookiesButton')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="secondary"
              onClick={onReject}
              size="small"
              variant="outlined"
              sx={bannerButtonSx}
            >
              {t('CookieConsent.rejectCookiesButton')}
            </Button>
          </Grid>
          <Grid item>
            <Link
              aria-controls="cookie-consent-panel"
              aria-expanded={showCookies}
              component="button"
              onClick={() => setShowCookies(!showCookies)}
              type="button"
              variant="body1"
            >
              {showCookies
                ? t('CookieConsent.hideMessageButton')
                : t('CookieConsent.viewMessageButton')}
            </Link>
          </Grid>
        </Grid>
        {showCookies && (
          <Box id="cookie-consent-panel" mt={3}>
            <Divider />
            <Typography variant="h6" component="h3" mt={3} mb={1}>
              {t('CookieConsent.panelTitle')}
            </Typography>
            <Typography variant="body1" mb={1}>
              {t('CookieConsent.panelDescription')}
            </Typography>
            <Link
              href={GOOGLE_ADVERTISING_PRODUCTS}
              rel="noopener noreferrer"
              target="_blank"
              variant="body1"
            >
              {t('CookieConsent.panelLink')}
            </Link>
          </Box>
        )}
      </Container>
    </RootBox>
  );
};
